import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, X, UserMinus, RotateCcw } from "lucide-react";
import { AvatarView } from "@/components/AvatarView";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";

export default function AttendanceTab({ classId, students, reload }) {
  const { t } = useI18n();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [records, setRecords] = useState({});
  const [sel, setSel] = useState(null);

  const load = useCallback(async () => {
    const data = await api.getAttendance(classId, { date });
    const map = {}; data.forEach((r) => (map[r.student_id] = r.status)); setRecords(map);
  }, [classId, date]);
  useEffect(() => { load(); }, [load]);

  const today = new Date().toISOString().slice(0, 10);
  const mark = async (sid, status) => { setRecords((r) => ({ ...r, [sid]: status })); await api.markAttendance({ class_id: classId, student_id: sid, date, status }); if (date === today) reload(); };
  const setStatus = async (sid, active_status) => { await api.updateStudent(sid, { active_status }); toast.success(active_status === "unsubscribed" ? "Alumno desapuntado" : "Alumno reactivado"); setSel(null); reload(); };
  const markAllPresent = async () => { const act = students.filter((s) => s.active_status !== "unsubscribed"); await Promise.all(act.map((s) => api.markAttendance({ class_id: classId, student_id: s.id, date, status: "present" }))); toast.success("Todos presentes"); load(); if (date === today) reload(); };

  const activos = students.filter((s) => s.active_status !== "unsubscribed");
  const desapuntados = students.filter((s) => s.active_status === "unsubscribed");
  const count = (st) => activos.filter((s) => records[s.id] === st).length;
  const selStudent = activos.find((s) => s.id === sel);

  const Big = ({ onClick, children, bg, fg, testid, activeOn }) => (
    <button data-testid={testid} onClick={onClick} className="h-14 px-5 rounded-2xl flex items-center justify-center gap-2 text-base font-extrabold transition-transform hover:scale-105 flex-1"
      style={activeOn ? { background: bg, color: "#fff" } : { background: "hsl(var(--muted))", color: fg }}>{children}</button>
  );

  return (
    <div className="space-y-5 pb-40 sm:pb-6">
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="attendance-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-2xl h-12 px-4 bg-card border-2 border-border font-bold" />
        <button data-testid="mark-all-present" onClick={markAllPresent} className="h-12 px-6 rounded-2xl bg-emerald-500 text-white font-extrabold hover:opacity-90">✓ {t("allPresent")}</button>
        <span className="px-4 h-12 rounded-2xl flex items-center gap-1.5 font-bold bg-emerald-500/15 text-emerald-600"><Check className="w-5 h-5" /> {count("present")}</span>
        <span className="px-4 h-12 rounded-2xl flex items-center gap-1.5 font-bold bg-red-500/15 text-red-500"><X className="w-5 h-5" /> {count("absent")}</span>
        <span className="px-4 h-12 rounded-2xl flex items-center gap-1.5 font-bold bg-muted text-muted-foreground"><UserMinus className="w-5 h-5" /> {desapuntados.length}</span>
      </div>

      {/* Mobile: tap a student → sticky confirm bar */}
      <div className="grid grid-cols-3 sm:hidden gap-3">
        {activos.map((s) => {
          const st = records[s.id];
          return (
            <button key={s.id} data-testid={`att-card-${s.id}`} onClick={() => setSel(s.id)}
              className={`rounded-3xl border-2 p-3 flex flex-col items-center gap-1 ${sel === s.id ? "border-primary bg-accent" : "border-border bg-card"} ${st === "absent" ? "opacity-60" : ""}`}>
              <AvatarView student={s} size={56} faded={st === "absent"} plain />
              <span className="text-xs font-bold text-center leading-tight truncate w-full">{s.name}</span>
              <span className={`text-[10px] font-extrabold ${st === "present" ? "text-emerald-500" : st === "absent" ? "text-red-500" : "text-muted-foreground"}`}>{st === "present" ? t("present") : st === "absent" ? t("absent") : "—"}</span>
            </button>
          );
        })}
      </div>

      {sel && selStudent && (
        <div data-testid="att-confirm-bar" className="sm:hidden fixed left-3 right-3 bottom-3 z-[80] bg-card rounded-3xl border-2 border-primary p-3 shadow-2xl space-y-2">
          <div className="flex items-center gap-3">
            <AvatarView student={selStudent} size={48} plain />
            <div className="font-display font-extrabold text-lg flex-1 truncate">{selStudent.name}</div>
            <button onClick={() => setSel(null)} className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2">
            <Big testid={`att-${selStudent.id}-present`} onClick={() => { mark(selStudent.id, "present"); setSel(null); }} bg="#00C853" fg="#00C853" activeOn={records[selStudent.id] === "present"}><Check className="w-5 h-5" /> {t("present")}</Big>
            <Big testid={`att-${selStudent.id}-absent`} onClick={() => { mark(selStudent.id, "absent"); setSel(null); }} bg="#FF3333" fg="#FF3333" activeOn={records[selStudent.id] === "absent"}><X className="w-5 h-5" /> {t("absent")}</Big>
          </div>
          <button data-testid={`att-${selStudent.id}-unsub`} onClick={() => window.confirm(`${t("unsubscribe")} · ${selStudent.name}?`) && setStatus(selStudent.id, "unsubscribed")}
            className="w-full h-11 rounded-2xl bg-muted text-muted-foreground font-bold flex items-center justify-center gap-2"><UserMinus className="w-4 h-4" /> {t("unsubscribe")}</button>
        </div>
      )}

      {/* Desktop rows */}
      <div className="hidden sm:block space-y-2">
        {activos.map((s) => {
          const st = records[s.id];
          return (
            <div key={s.id} data-testid={`attendance-row-${s.id}`} className={`flex items-center gap-3 bg-card rounded-2xl border-2 border-border p-3 ${st === "absent" ? "opacity-70" : ""}`}>
              <AvatarView student={s} size={48} faded={st === "absent"} plain />
              <span className="flex-1 font-extrabold text-base truncate">{s.name}</span>
              <button data-testid={`att-row-${s.id}-present`} onClick={() => mark(s.id, "present")} className="h-12 px-4 rounded-2xl flex items-center gap-1.5 font-bold transition-transform hover:scale-105" style={st === "present" ? { background: "#00C853", color: "#fff" } : { background: "hsl(var(--muted))", color: "#00C853" }}><Check className="w-5 h-5" /> {t("present")}</button>
              <button data-testid={`att-row-${s.id}-absent`} onClick={() => mark(s.id, "absent")} className="h-12 px-4 rounded-2xl flex items-center gap-1.5 font-bold transition-transform hover:scale-105" style={st === "absent" ? { background: "#FF3333", color: "#fff" } : { background: "hsl(var(--muted))", color: "#FF3333" }}><X className="w-5 h-5" /> {t("absent")}</button>
              <button data-testid={`att-row-${s.id}-unsub`} onClick={() => window.confirm(`${t("unsubscribe")} · ${s.name}?`) && setStatus(s.id, "unsubscribed")} className="h-12 px-4 rounded-2xl flex items-center gap-1.5 font-bold bg-muted text-muted-foreground hover:bg-muted-foreground hover:text-white"><UserMinus className="w-5 h-5" /> {t("unsubscribe")}</button>
            </div>
          );
        })}
        {activos.length === 0 && <div className="text-center text-muted-foreground py-10">{t("noActive")}</div>}
      </div>

      {desapuntados.length > 0 && (
        <div>
          <h3 className="font-display font-bold text-sm text-muted-foreground uppercase tracking-wide mb-2">{t("unsubscribed")}</h3>
          <div className="space-y-2">
            {desapuntados.map((s) => (
              <div key={s.id} className="flex items-center gap-3 bg-card rounded-2xl border-2 border-dashed border-border p-3 opacity-80">
                <AvatarView student={s} size={44} faded plain />
                <span className="flex-1 font-bold truncate">{s.name}</span>
                <button data-testid={`reactivate-${s.id}`} onClick={() => setStatus(s.id, "active")} className="h-11 px-5 rounded-2xl flex items-center gap-1.5 font-bold bg-primary text-primary-foreground"><RotateCcw className="w-4 h-4" /> {t("reactivate")}</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
